import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FiX, FiFileText, FiTrash2, FiInbox } from "react-icons/fi";
import { JSONContent } from "@tiptap/react";
import { apiService } from "../../services/apiService";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const InboxIcon = FiInbox as React.FC<React.SVGProps<SVGSVGElement>>;

interface Draft {
  id: string;
  subject: string;
  content: JSONContent | null;
  workflow_config: any;
  selected_integrations: string[];
  created_at: string;
  updated_at: string;
}

interface ChooseDraftModalProps {
  onClose: () => void;
  onSelect: (draft: Draft) => void;
}

/**
 * Extract plain text preview from TipTap JSON content
 */
function extractTextPreview(content: JSONContent | null): string {
  if (!content) return "Empty draft";

  // Recursively extract text from TipTap JSON
  const extractText = (node: any): string => {
    if (node.text) return node.text;
    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractText).join(" ");
    }
    return "";
  };

  const fullText = extractText(content).trim();
  if (!fullText) return "Empty draft";

  const preview = fullText.substring(0, 100);
  return fullText.length > 100 ? preview + "..." : preview;
}

/**
 * Format timestamp as relative time
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

  return date.toLocaleDateString();
}

/**
 * Truncate long subject lines
 */
function truncateSubject(subject: string): string {
  if (!subject) return "Untitled Draft";
  return subject.length > 60 ? subject.substring(0, 60) + "..." : subject;
}

/**
 * ChooseDraftModal - Draft selection modal
 * Displays saved email drafts with preview and allows selection
 */
const ChooseDraftModal: React.FC<ChooseDraftModalProps> = ({
  onClose,
  onSelect,
}) => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getDrafts();
      if (response.success && response.data) {
        setDrafts(response.data);
      }
    } catch (error) {
      console.error("Failed to load draft:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDraftClick = (draft: Draft) => {
    setSelectedId(draft.id);
    onSelect(draft);
  };

  const handleDelete = async (e: React.MouseEvent, draftId: string) => {
    e.stopPropagation(); // Prevent draft selection when clicking delete

    try {
      await apiService.deleteDraft(draftId);
      // Refresh the list after deletion
      await loadDrafts();
    } catch (error) {
      console.error("Failed to delete draft:", error);
      alert("Failed to delete draft. Please try again.");
    }
  };

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal su-template-modal">
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <FileTextIcon />
            <span>CHOOSE A DRAFT</span>
          </div>
          <button type="button" className="su-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body su-template-modal-body">
          <p className="su-template-subtitle">
            Select a saved draft to continue working on it.
          </p>

          {isLoading ? (
            <div className="su-draft-empty-state">
              <p>Loading drafts...</p>
            </div>
          ) : drafts.length === 0 ? (
            <div className="su-draft-empty-state">
              <InboxIcon className="su-draft-empty-state-icon" />
              <h3 className="su-draft-empty-state-title">No Drafts Yet</h3>
              <p className="su-draft-empty-state-description">
                Save your work as a draft to access it later.
              </p>
            </div>
          ) : (
            <div className="su-template-grid">
              {drafts.map((draft) => (
                <button
                  key={draft.id}
                  type="button"
                  className={`su-template-card ${selectedId === draft.id ? "selected" : ""}`}
                  onClick={() => handleDraftClick(draft)}
                >
                  <div className="su-template-card-content">
                    <h5 className="su-template-card-title">
                      {truncateSubject(draft.subject)}
                    </h5>
                    <p className="su-template-card-description">
                      {extractTextPreview(draft.content)}
                    </p>
                    <div className="su-draft-card-meta">
                      <span className="su-draft-card-timestamp">
                        {formatRelativeTime(draft.updated_at)}
                      </span>
                      <button
                        type="button"
                        className="su-draft-card-delete"
                        onClick={(e) => handleDelete(e, draft.id)}
                        title="Delete draft"
                      >
                        <TrashIcon className="w-5 h-5" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="su-modal-footer">
          <button
            type="button"
            className="su-template-cancel-btn"
            onClick={onClose}
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChooseDraftModal;
